<script setup>
  import { inject, ref, onMounted, reactive, watch, nextTick } from 'vue'
  import AssessmentSteps from './AssessmentSteps.vue'
  import Footer from './FooterSection.vue'
  import { vElementVisibility } from '@vueuse/components'
  import { useQuestionsStore } from '../stores/QuestionsStore'
  import { usePositionStateStore } from '../stores/PositionStateStore'
  import { storeToRefs } from 'pinia'
  import BcsDynamicText from './BcsDynamicText.vue'
  import TextWithDefinitions from './TextWithDefinitions.vue'
  import { markCheckbox } from '../utils'
  import { getVisibleOptions } from '../utils/questionsAndAnswersHelpers'
  import { useEmployerStore } from '../stores/EmployerStore'
  import { vMaska } from 'maska'
  import { RequestService } from '../services/RequestService'
  import { ScoreCalculationsService } from '../services/ScoreCalculationsService'
  import { ComplianceAuditLogService } from '../services/ComplianceAuditLogService'
  import { AgencyDataService } from '../services/AgencyDataService'
  import AlertNotification from './AlertNotification.vue'
  import { sortBy, isEqual, some, isEmpty, uniq, get } from 'lodash'
  import PlanYearDropdown from '@/shared/components/common/PlanYearDropdown.vue'

  const emitter = inject('emitter')

  const customNavigation = inject('customNavigation')

  const questionStore = useQuestionsStore()
  const positionStateStore = usePositionStateStore()

  const { assessmentQuestions, assessKeys, erisaDate, planQuestions, isLoading, previousActionItemIds, lastSavedActionItemIds } =
    storeToRefs(questionStore)

  const {
    saveQuestions,
    clearQuestions,
    showQuestion,
    hideDueICHRA,
    getCompletionStatus,
    isAssessmentComplete,
    isValidDate,
    getAnswersByQuestionIds,
    getLiveAnswerById,
    clearOrphanConditionalAnswers
  } = questionStore

  const employerStore = useEmployerStore()
  const { actualEmployer, actualPlanYearId } = storeToRefs(employerStore)
  const { createLog, handleErrors } = RequestService()
  const { saveScore } = ScoreCalculationsService()
  const { checkHasInitialSnapshot, recordInitialSnapshot, recordAnswerChange } = ComplianceAuditLogService()

  const rawMaskVal = reactive({})

  const { setPositionState, getPositionState } = positionStateStore

  const componentKey = ref(0)
  const showAlertClearAssessment = ref(false)

  const visibleSectionsRefs = ref([])
  const scrollContRef = ref()

  const saving = ref(false)

  const lastTimeSavedAssessment = ref(null)
  const timeElapsedAssessment = ref('Not auto-saved yet ')
  const idSetIntervalSecondAssessment = ref(null)
  const idSetIntervalMinuteAssessment = ref(null)
  const planErisaAnswer = ref()

  const hideText = new Map([])

  const assessmentWasCompleted = ref(false)

  async function initAuditTrailFlag() {
    if (isAssessmentComplete()) {
      assessmentWasCompleted.value = true
      return
    }
    try {
      const hasSnapshot = await checkHasInitialSnapshot(actualPlanYearId.value)
      if (hasSnapshot) assessmentWasCompleted.value = true
    } catch (err) {
      handleErrors('initAuditTrailFlag', err)
    }
  }

  // One save in flight at a time; later calls queue (one per noteId) and run with the latest
  // state afterwards, so an older full-snapshot save can never overwrite a newer one.
  let saveInFlight = false
  const pendingSaves = []

  async function saveAssessment({ noteId = null }) {
    if (saveInFlight) {
      if (!pendingSaves.some((p) => p.noteId === noteId)) pendingSaves.push({ noteId })
      return
    }
    saveInFlight = true
    try {
      await runSaveAssessment({ noteId })
    } finally {
      saveInFlight = false
    }
    if (pendingSaves.length > 0) await saveAssessment(pendingSaves.shift())
  }

  async function runSaveAssessment({ noteId = null }) {
    saving.value = true
    try {
      const { markAgencyDataUpdated } = AgencyDataService()
      const complianceBefore = {
        total_compliance_risk: actualEmployer.value.total_compliance_risk ?? null,
        percentage_compliance_risk: actualEmployer.value.percentage_compliance_risk ?? null
      }
      const prevAnswers = getAnswersByQuestionIds([
        ...previousActionItemIds.value,
        ...lastSavedActionItemIds.value
      ])

      // Mark agency data as updated for Action Center cache invalidation
      markAgencyDataUpdated(actualEmployer.value.agency_id)

      // Drop conditional answers whose condition no longer holds before persisting.
      clearOrphanConditionalAnswers()
      await saveQuestions('assessment', noteId)
      saving.value = false
      componentKey.value += 1
      const ASSESSMENT_UPDATED = '6'
      const ASSESSMENT_COMPLETED = '19'

      const logEntry = {
        eventId: ASSESSMENT_UPDATED,
        logName: 'user_activity_log_v2',
        employerId: actualEmployer.value.id,
        planYearId: actualPlanYearId.value,
        agencyId: actualEmployer.value.agency_id
      }

      const assessmentComplete = isAssessmentComplete()
      let complianceAfter = complianceBefore

      if (assessmentComplete) {
        logEntry.eventId = ASSESSMENT_COMPLETED
        assessmentWasCompleted.value = true
        const scoreResult = await saveScore(actualEmployer.value.id)
        if (scoreResult) {
          complianceAfter = {
            total_compliance_risk: scoreResult.totalComplianceRisk,
            percentage_compliance_risk: parseFloat(scoreResult.percentageComplianceRisk)
          }
        }
      }

      if (assessmentWasCompleted.value) {
        processComplianceAuditLog(complianceBefore, complianceAfter, prevAnswers)
      }

      createLog(logEntry)
    } catch (err) {
      saving.value = false
      handleErrors('saveAssessment', err)
    }
  }

  async function processComplianceAuditLog(complianceBefore, complianceAfter, prevAnswers) {
    try {
      const employerId = actualEmployer.value.id
      const planYearId = actualPlanYearId.value
      const agencyId = actualEmployer.value.agency_id
      const prevIds = previousActionItemIds.value.map(String)
      const newIds = lastSavedActionItemIds.value.map(String)

      const hasSnapshot = await checkHasInitialSnapshot(planYearId)

      if (!hasSnapshot) {
        const allRelevantIds = [...new Set([...prevIds, ...newIds])]
        const currentAnswers = getAnswersByQuestionIds(allRelevantIds)
        await recordInitialSnapshot({
          employerId,
          planYearId,
          agencyId,
          actionItemQuestionIds: newIds,
          complianceSnapshotAfter: complianceAfter,
          questionsMap: currentAnswers
        })
        return
      }

      const resolved = prevIds.filter(id => !newIds.includes(id))
      const created = newIds.filter(id => !prevIds.includes(id))

      const currentAnswers = getAnswersByQuestionIds([...resolved, ...created])

      for (const questionId of resolved) {
        await recordAnswerChange({
          employerId,
          planYearId,
          agencyId,
          questionId,
          source: 'assessment_tool',
          changeType: 'action_item_resolved',
          previousAnswer: prevAnswers[questionId] ?? null,
          newAnswer: currentAnswers[questionId] ?? null,
          previousActionItemIds: prevIds,
          newActionItemIds: newIds,
          complianceSnapshotBefore: complianceBefore,
          complianceSnapshotAfter: complianceAfter
        })
      }

      for (const questionId of created) {
        await recordAnswerChange({
          employerId,
          planYearId,
          agencyId,
          questionId,
          source: 'assessment_tool',
          changeType: 'action_item_created',
          previousAnswer: prevAnswers[questionId] ?? null,
          newAnswer: currentAnswers[questionId] ?? null,
          previousActionItemIds: prevIds,
          newActionItemIds: newIds,
          complianceSnapshotBefore: complianceBefore,
          complianceSnapshotAfter: complianceAfter
        })
      }
    } catch (err) {
      handleErrors('processComplianceAuditLog', err)
    }
  }

  function autoSaveAssessment({ noteId = null }) {
    saveAssessment({ noteId })
    lastTimeSavedAssessment.value = new Date()
    timeElapsedAssessment.value = 'Auto-saved just now'

    if (idSetIntervalSecondAssessment.value) {
      clearInterval(idSetIntervalSecondAssessment.value)
    }
    if (idSetIntervalMinuteAssessment.value) {
      clearInterval(idSetIntervalMinuteAssessment.value)
    }
    let countSeconds = 0
    idSetIntervalSecondAssessment.value = setInterval(() => {
      countSeconds++
      let now = new Date()
      let diff = now - lastTimeSavedAssessment.value
      let seconds = Math.floor(diff / 1000)
      timeElapsedAssessment.value = `Auto-saved ${seconds} secs ago`
      if (countSeconds > 59) {
        clearInterval(idSetIntervalSecondAssessment.value)
        idSetIntervalMinuteAssessment.value = setInterval(() => {
          let now = new Date()
          let diff = now - lastTimeSavedAssessment.value
          let minutes = Math.floor(diff / 60000)
          timeElapsedAssessment.value = `Auto-saved ${minutes} mins ago`
        }, 60000)
      }
    }, 10000)
  }

  function generateReport() {
    saveAssessment({})
    customNavigation.value.navigate({
      newPage: customNavigation.value.currentPage,
      subPage: customNavigation.value.routes.DetailEmployer.children[3].name
    })
  }

  function clearAssessment() {
    clearQuestions('assessment')
    showAlertClearAssessment.value = false
    saveAssessment({})
  }

  onMounted(() => {
    initAuditTrailFlag()
    if (!isLoading) {
      planErisaAnswer.value = Object.values(planQuestions.value)[0][1].answer
    }
    const scrollPosition = getPositionState(actualEmployer.value.planYearId, 'assessmentTool')
    if (scrollPosition && formAssessment.value instanceof HTMLElement) {
      formAssessment.value.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      })
    }

    console.log('customNavigation.value.internalQuery.questionId', customNavigation.value.internalQuery.questionId)
    if (customNavigation.value.internalQuery.questionId) {
    nextTick(() => {
      const questionEl = document.getElementById(`question-${customNavigation.value.internalQuery.questionId}`)
      if (questionEl) {
        questionEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Optionally highlight the question
      }
    })
  }
  })

  watch(
    () => isLoading.value,
    (newIsLoading) => {
      if (!newIsLoading) {
        planErisaAnswer.value = Object.values(planQuestions.value)[0][1].answer
      }
    }
  )

  const formAssessment = ref(null)
  const lastVisibleQuestion = ref('q-0')

  const controlVisibilityOfSection = ref([])
  function onElementVisibilityParent(keyIndex, isVisible) {
    controlVisibilityOfSection.value[keyIndex] = isVisible
  }

  function getVisibleSection(reInvoke = true) {
    const scrollPosition = get(formAssessment.value, 'scrollTop', 0)
    setPositionState(actualEmployer.value.planYearId, 'assessmentTool', scrollPosition)
    // The variable "controlVisibilityOfSection" is an array of booleans that controls if the section is visible
    // The index of the array corresponds to the index of the section
    // Then I create an array with the indices of the sections that are visible and then filter the indices that remained
    let indexVisibleSections = controlVisibilityOfSection.value
      .map((isVisible, index) => {
        return isVisible ? index : -1
      })
      .filter((index) => index > -1)
    // Then I get the bounding of each visible section and get the top of each one
    let topOfVisibleSections = {}
    indexVisibleSections.forEach((index) => {
      let section = visibleSectionsRefs.value[index]
      let bounding = section.getBoundingClientRect()
      topOfVisibleSections[index] = bounding.top
    })
    // To then know which is the section closest to the top of the screen
    let closest = Object.keys(topOfVisibleSections).reduce((indexA, indexB) => {
      if (topOfVisibleSections[indexA] > 150 && topOfVisibleSections[indexA] < 350) {
        return indexA
      }
      if (topOfVisibleSections[indexB] > 150 && topOfVisibleSections[indexB] < 350) {
        return indexB
      }
      return Math.abs(topOfVisibleSections[indexA]) < Math.abs(topOfVisibleSections[indexB]) ?
          indexA
        : indexB
    }, 0)
    // And finally update the value of the variable "lastVisibleQuestion" with the id of the closest section
    lastVisibleQuestion.value = `q-${closest}`
    if (reInvoke) {
      setTimeout(() => {
        getVisibleSection(false)
      }, 50)
    }
  }

  const getClass = (key, keyIndex) => {
    return {
      active: lastVisibleQuestion.value === `q-${keyIndex}`,
      [getCompletionStatus(key, keyIndex)]: true
    }
  }

  function handleClickCheckBox(event, question, optionIndex) {
    if (!event.target.closest('.words-for-definition')) {
      markCheckbox(question, optionIndex)
      autoSaveAssessment({})
    }
  }

  function handleRadioClick(event, question, optionIndex) {
    if (!event.target.closest('.words-for-definition')) {
      question.answer = optionIndex
      // Changing an answer may invalidate conditional options elsewhere (e.g. "N/A" on Q3-Q5).
      clearOrphanConditionalAnswers()
      autoSaveAssessment({})
    }
  }

  // Radio options to render; conditional options show only while their condition holds (original indexes kept).
  function visibleOptions(question) {
    return getVisibleOptions(question, getLiveAnswerById)
  }

  function isHiddenGroupStart(questions, index) {
    if (index === 0) {
      return true
    } else if (showQuestion(questions[index - 1]).show) {
      return true
    } else if (!isEqual(sortBy(showQuestion(questions[index - 1]).questionsHideReasons), sortBy(showQuestion(questions[index]).questionsHideReasons))) {
      return true
    } else {
      return false
    }
  }

  function getHiddenGroupRange(questions, startIndex) {
    let endIndex = startIndex
    let id = questions[startIndex].id
    let reasons = []
    let questionsHideReasons = null

    let startObj = showQuestion(questions[startIndex])
    while (endIndex < questions.length) {
      let endObj = showQuestion(questions[endIndex])
      if (!endObj.show && isEqual(sortBy(startObj.questionsHideReasons), sortBy(endObj.questionsHideReasons))) {
        if (!isEmpty(endObj.reasons)) {
          reasons.push(...endObj.reasons)

          if (questionsHideReasons === null) {
            questionsHideReasons = endObj.questionsHideReasons
          }
        }
        endIndex++
      } else {
        break
      }
    }

    let message =
      some(questionsHideReasons, (questionId) => questionId <= 13) ?
      'hidden due to answer(s) provided on plan questionnaire'
      : 'hidden due to previously provided answer(s) on assessment'

    if (!isEmpty(reasons)) {
      hideText.set('' + id, uniq(reasons).join('\n\n'))
    }

    if (endIndex === startIndex + 1) {
      return `Question ${startIndex + 1} is ${message}`
    }
    return `Questions ${startIndex + 1}-${endIndex} ${message}`
  }
</script>

<template>
  <div class="col-10 px-0">
    <div class="mt-0 px-0">
      <form
        class="wizard-form scroll-fix"
        ref="formAssessment"
        @submit.prevent="() => saveAssessment({})"
        @scroll="getVisibleSection">
        <div class="steps-banner">
          <div class="container-fluid px-3">
            <div class="row">
              <div class="col-8">
                <div class="d-flex align-items-center">
                  <span class="my-3 text-secondary me-3 text-28">
                    <med>
                      {{ actualEmployer.name }} BCS Assessment Tool
                    </med>
                  </span>
                  <div class="align-self-start mt-3.5">
                    <PlanYearDropdown />
                  </div>
                </div>
                <span class="py-2 text-14">
                  <BcsDynamicText text="assessmentToolInstructionalText" />
                </span>
              </div>
              <div class="col-4">
                <div class="d-flex justify-content-end">
                  <!-- <a
                    class="hand-cursor d-inline my-3 px-2 border-end text-decoration-none text-14 text-primary"
                    data-bs-toggle="modal"
                    data-bs-target="#pendingFunctionModal">
                    <strong>View Sample Report</strong>
                  </a> -->
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="steps-banner sticky-top pt-3 mt-3">
          <div class="row">
            <AssessmentSteps
              :key="componentKey"
              :activeStep="lastVisibleQuestion">
            </AssessmentSteps>
            <div class="col-2">
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{ saving ? 'Saving...' : 'Save Draft' }}
                <i class="bi bi-arrow-right-short"></i>
              </button>
              <br />
              <span class="text-12 text-end d-inline-block">{{ timeElapsedAssessment }}</span>
            </div>
          </div>
          <hr />
        </div>
        <div class="form-container scroll-padding px-0" ref="scrollContRef">
          <template v-for="(key, keyIndex) in assessKeys" :key="keyIndex">
            <div
              :class="`row mx-3`"
              ref="visibleSectionsRefs"
              v-if="!hideDueICHRA(assessmentQuestions[key][0]['short_title'])"
              :id="keyIndex">
              <div class="col-1 w-auto pt-1">
                <span
                  class="scroll-padding circle"
                  :id="keyIndex + 1"
                  :class="getClass(key, keyIndex)">
                  {{ keyIndex + 1 }}
                </span>
              </div>
              <div
                class="col-11"
                v-element-visibility="[
                  (state) => onElementVisibilityParent(keyIndex, state),
                  { scrollTarget: formAssessment }
                ]">
                <span :id="`q-${keyIndex}`" class="pt-2 header-22 d-block mb-3">
                  <strong>
                    <TextWithDefinitions :text="assessmentQuestions[key][0]['subtitle']" />
                  </strong>
                </span>
                <span class="pb-3 text-16">
                  {{ key }}
                </span>
                <div
                  class="alert-assesment"
                  v-if="assessmentQuestions[key][0]['id'] === 13 && planErisaAnswer === 1">
                  <AlertNotification :showCloseButton="false" typeAlert="info">
                    <strong><BcsDynamicText text="erisaAlertNotification" /></strong>
                  </AlertNotification>
                </div>
                <div
                  v-for="(question, questionIndex) in assessmentQuestions[key]"
                  :class="showQuestion(question).show ? 'my-3' : 'my-2'"
                  :key="questionIndex"
                  :id="`question-${question.id}`">
                  <div class="row pt-2" v-if="showQuestion(question).show">
                    <div class="col-5">
                      <span class="question-title text-14">
                        <strong>
                          <TextWithDefinitions
                            :text="`Q${questionIndex + 1}: ${question.question}`" />
                        </strong>
                      </span>
                      <div
                        class="mt-2 erisa-date-container"
                        :class="{ selected: question.answer === 0 && question.type === 4 }">
                        <template v-if="question.type === 0">
                          <div
                            v-for="option in visibleOptions(question)"
                            class="form-check form-check-inline"
                            :key="option.index"
                            @click="handleRadioClick($event, question, option.index)">
                            <input
                              class="form-check-input"
                              type="radio"
                              :id="question.id + '' + option.index + 'radio'"
                              v-model="question.answer"
                              :value="option.index"
                              :checked="question.answer === option.index"
                            />
                            <label class="form-check-label text-16">
                              <TextWithDefinitions :text="option.label" />
                            </label>
                          </div>
                        </template>
                        <template v-else-if="question.type === 1">
                          <div
                            v-for="(option, optionIndex) in question.options"
                            class="form-check"
                            :key="option"
                            @click="handleClickCheckBox($event, question, optionIndex)">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="question.id + '' + optionIndex + 'check'"
                              v-model="question.answer"
                              :value="optionIndex" />
                            <label class="form-check-label text-16">
                              <TextWithDefinitions :text="option" />
                            </label>
                          </div>
                        </template>
                        <div v-else-if="question.type === 2">
                          <select
                            name="cars"
                            class="form-select"
                            @change="() => autoSaveAssessment({})"
                            v-model="question.answer">
                            <option
                              v-for="(option, i) in question.options"
                              :value="option"
                              class="text-16"
                              :key="i">
                              <TextWithDefinitions :text="option" />
                            </option>
                          </select>
                        </div>
                        <template v-if="question.type === 4">
                          <div
                            v-for="(option, optionIndex) in question.options"
                            class="form-check form-check-inline"
                            :key="optionIndex"
                            @click="(question.answer = optionIndex), autoSaveAssessment({})">
                            <input
                              class="form-check-input"
                              type="radio"
                              :id="question.id + '' + optionIndex + 'radio'"
                              v-model="question.answer"
                              :value="optionIndex" />
                            <label class="form-check-label text-16">
                              <TextWithDefinitions :text="option" />

                              <input
                                v-if="
                                  optionIndex === 0 &&
                                  (question.answer === 0 || question.answer > 10)
                                "
                                type="text"
                                class="form-control text-16 prueba-2"
                                v-model="erisaDate"
                                v-maska="rawMaskVal"
                                data-maska="##/##/####"
                                placeholder="e.g., MM/DD/YYYY"
                                :class="{ 'is-invalid': !isValidDate(erisaDate) }"
                                @change="autoSaveAssessment({})" />
                            </label>
                          </div>
                        </template>
                      </div>
                    </div>
                    <div class="col-7">
                      <span class="text-14"><strong>Notes</strong></span>
                      <textarea
                        class="form-control mt-2"
                        id="exampleFormControlTextarea1"
                        rows="3"
                        v-model="question.notes"
                        @change="() => autoSaveAssessment({ noteId: question.id })"></textarea>
                    </div>
                  </div>
                  <div v-else-if="isHiddenGroupStart(assessmentQuestions[key], questionIndex)">
                    <div class="row">
                      <div class="col-12">
                        <div class="text-14 p-3 bg-gray-100 text-gray-500">
                          <strong>
                            {{
                              getHiddenGroupRange(assessmentQuestions[key], questionIndex) + '   '
                            }}
                          </strong>
                          <span
                            class="show-question-link ml-1"
                            id="hide-reasonLink"
                            aria-controls="#hide-reason"
                            @click="
                              emitter.emit('toggleOffCanvas', {
                                id: hideText.get('' + question.id),
                                definitionExpected: 'Reason for hidden question(s)'
                              })
                            ">
                            Show reason(s)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
            </div>
          </template>
          <div class="d-flex justify-content-end mb-4">
            <button
              id="clearFormButtonAssessment"
              type="button"
              class="btn btn-info me-3"
              @click="showAlertClearAssessment = true">
              <i class="bi bi-x-circle"></i>
              Clear Form
            </button>

            <button
              id="generateReportButtonAssessment"
              type="button"
              :disabled="!isAssessmentComplete()"
              class="btn btn-primary me-3"
              @click="generateReport">
              Generate Report
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div class="row d-flex mt-3 2">
            <div class="col-12 p-0">
              <Footer class="px-5 mx-n2"></Footer>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>

  <div v-if="showAlertClearAssessment" class="col-10 overlay-white-left">
    <div class="card mt-5 d-flex mx-auto">
      <div
        class="card-body text-center d-flex flex-column justify-content-center align-items-center">
        <h5 class="card-title header-22">
          <i class="bi bi-exclamation-circle-fill text-danger"></i>
          &nbsp;
          <strong>Warning</strong>
        </h5>
        <p class="card-text text-14 text-gray-700">
          Are you sure you want to clear all assessment answers? This cannot be undone.
        </p>

        <div class="d-flex justify-content-end mb-4">
          <button type="button" class="btn btn-info me-3" @click="showAlertClearAssessment = false">
            Cancel
          </button>

          <button type="button" class="btn btn-primary me-3" @click="clearAssessment">
            Yes, Clear All Answers
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .form-check-input {
    border: 1px solid var(--bs-gray-600, #6c757d) !important;
  }

  .question-title {
    flex-basis: 100%;
  }

  .floating-draft {
    position: relative;
    right: -15.5vw;
  }

  .steps-banner {
    background-color: white;
  }

  .wizard-container {
    padding: 0 10px;
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  .step-container {
    width: 10%;
  }

  .circle {
    line-height: 45px;
    -moz-border-radius: 50%;
    border: solid 3px;
    border-color: var(--bs-primary);
    background-color: #ffffff;
    color: var(--bs-primary);
    text-align: center;
    font-size: 20px;

    width: 30px; /* Adjust circle size */
    height: 30px; /* Adjust circle size */
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
  }

  .circle.filled {
    background-color: var(--bs-dark);
    color: white;
    border: 2px solid var(--bs-dark);
  }

  .circle.active {
    box-shadow: 0 0 0 4px #7b61ff78;
  }

  .empty {
    div {
      background-color: #ffffff; /* Adjust empty circle color */
      border: 2px solid var(--bs-secondary); /* Adjust empty circle border color */
      i {
        visibility: hidden;
      }
    }
    p {
      color: var(--bs-secondary);
    }
  }

  .partial {
    div {
      box-shadow:
        inset 0 0 0 8px #ffffff,
        inset 0 0 0 80px #007bff;
      i {
        visibility: hidden;
      }
    }
  }

  .scroll-padding {
    scroll-padding-top: 13vh;
    scroll-margin-top: 13vh;
  }

  .scroll-fix {
    overflow-y: scroll;
    overflow-x: hidden;
    max-height: 94vh;
  }

  //For tmp hjiding reason off canvas
  .box-shadow-offcanvas-definition {
    box-shadow: -5px 0px 8px 0px #0000001a;
  }

  .words-for-definition {
    color: var(--bs-primary);
    cursor: pointer;
    text-decoration: none;
    border-bottom: 3px dotted var(--bs-primary);
    font-weight: bold;
  }

  .show-question-link {
    color: var(--bs-primary);
    cursor: pointer;
    text-decoration: underline;
  }

  .erisa-date-container {
    .form-check-inline {
      display: inline-block;
      margin-right: 1rem;
    }

    &.selected {
      .form-check-inline:not(:first-child) {
        display: block;
        margin-right: 0;
        margin-top: 0.5rem;
      }

      .erisa-date-input {
        display: block;
        margin-top: 0.5rem;
        margin-left: 1.5rem;
      }
    }
  }

  .alert-assesment {
    margin-top: 13px;
  }
</style>
